import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Briefcase, MapPin, Clock } from 'lucide-react';

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);

  const openPositions = [
    {
      id: 1,
      title: 'Amazon Account Manager',
      department: 'Operations',
      location: 'Indian Trail, NC',
      type: 'Full-time',
      description: 'Manage day-to-day Amazon seller operations for multiple client accounts. Optimize listings, monitor performance metrics, and coordinate with vendors.',
      requirements: [
        '3+ years Amazon seller experience',
        'Strong understanding of Amazon FBA',
        'Excellent communication skills',
        'Data-driven decision making'
      ]
    },
    {
      id: 2,
      title: 'PPC Specialist',
      department: 'Marketing',
      location: 'Indian Trail, NC / Remote',
      type: 'Full-time',
      description: 'Develop and optimize Amazon PPC campaigns using our AI-powered platform. Analyze performance data and implement strategies to maximize ROI.',
      requirements: [
        '2+ years Amazon PPC experience',
        'Proficiency in campaign analytics',
        'Understanding of keyword research',
        'Experience with bid optimization'
      ]
    },
    {
      id: 3,
      title: 'Warehouse Operations Coordinator',
      department: 'Logistics',
      location: 'Indian Trail, NC',
      type: 'Full-time',
      description: 'Oversee receiving, inspection, labeling, and FBA preparation of inventory. Ensure Amazon compliance and coordinate shipments to fulfillment centers.',
      requirements: [
        'Experience in warehouse management',
        'Knowledge of FBA requirements',
        'Strong organizational skills',
        'Ability to manage multiple priorities'
      ]
    },
    {
      id: 4,
      title: 'Business Development Manager',
      department: 'Sales',
      location: 'Remote',
      type: 'Full-time',
      description: 'Identify and develop new vendor partnerships. Present EYME LLC services to potential clients and negotiate partnership agreements.',
      requirements: [
        '5+ years B2B sales experience',
        'E-commerce industry knowledge',
        'Proven track record of closing deals',
        'Excellent presentation skills'
      ]
    },
    {
      id: 5,
      title: 'Data Analyst',
      department: 'Technology',
      location: 'Remote',
      type: 'Full-time',
      description: 'Analyze sales data, market trends, and campaign performance. Develop insights and recommendations to drive business growth.',
      requirements: [
        'Strong SQL and Python skills',
        'Experience with data visualization tools',
        'E-commerce analytics experience preferred',
        'Bachelor\'s degree in related field'
      ]
    },
    {
      id: 6,
      title: 'Customer Success Specialist',
      department: 'Customer Success',
      location: 'Indian Trail, NC',
      type: 'Full-time',
      description: 'Serve as primary contact for vendor partners. Handle inquiries, resolve issues, and ensure high levels of client satisfaction.',
      requirements: [
        'Excellent customer service skills',
        'Problem-solving abilities',
        'E-commerce experience preferred',
        'Strong written and verbal communication'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2V8ZW58MHx8fHwxNzcyMjcwNjc2fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/85"></div>
        <div className="container mx-auto px-6 relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white text-center mb-6">
            Build the Future of <span className="text-[#FF9900]">E-Commerce</span> With Us
          </h1>
          <p className="text-xl text-gray-200 text-center max-w-3xl mx-auto">
            Join a team that's revolutionizing how brands succeed on Amazon and beyond
          </p>
        </div>
      </section>

      {/* Company Culture */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Why Work at <span className="text-[#FF9900]">EYME LLC</span>?
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-12">
              We're not just another e-commerce company—we're a team of passionate professionals 
              dedicated to helping brands thrive in the digital marketplace. At EYME LLC, you'll work 
              with cutting-edge technology, collaborate with talented colleagues, and make a real impact 
              on the success of thousands of businesses.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-4">🚀</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Growth Opportunities</h3>
                  <p className="text-gray-600">
                    Advance your career with continuous learning, mentorship, and clear paths to leadership roles.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-4">💼</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Competitive Benefits</h3>
                  <p className="text-gray-600">
                    Comprehensive health insurance, 401(k) matching, generous PTO, and performance bonuses.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-4">🌟</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Innovative Culture</h3>
                  <p className="text-gray-600">
                    Work with the latest technology and contribute ideas that shape our company's future.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-slate-900 mb-4 text-center">
            Open <span className="text-[#FF9900]">Positions</span>
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            Find your next career opportunity at EYME LLC
          </p>

          <div className="max-w-5xl mx-auto space-y-6">
            {openPositions.map((job) => (
              <Card 
                key={job.id} 
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">{job.title}</h3>
                      <div className="flex flex-wrap gap-4 text-gray-600">
                        <div className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-2" />
                          {job.department}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          {job.type}
                        </div>
                      </div>
                    </div>
                    <Button className="mt-4 md:mt-0 bg-[#FF9900] hover:bg-[#e68a00] text-white">
                      {selectedJob === job.id ? 'Hide Details' : 'View Details'}
                    </Button>
                  </div>

                  {selectedJob === job.id && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="text-lg font-bold text-slate-900 mb-3">Description</h4>
                      <p className="text-gray-700 mb-6">{job.description}</p>
                      
                      <h4 className="text-lg font-bold text-slate-900 mb-3">Requirements</h4>
                      <ul className="space-y-2 mb-6">
                        {job.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start text-gray-700">
                            <div className="w-2 h-2 bg-[#FF9900] rounded-full mr-3 mt-2"></div>
                            {req}
                          </li>
                        ))}
                      </ul>

                      <Button className="bg-[#FF9900] hover:bg-[#e68a00] text-white">
                        Apply Now
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Resume */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Don't See the Right <span className="text-[#FF9900]">Position</span>?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            We're always interested in meeting talented professionals. Send us your resume and 
            we'll keep you in mind for future opportunities.
          </p>
          <a href="/contact">
            <Button className="bg-[#FF9900] hover:bg-[#e68a00] text-white px-10 py-4 text-lg font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105">
              Submit Your Resume
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Careers;
